/**
 * Radar de Desempenho Query Hooks
 *
 * TanStack Query hooks for the Radar de Desempenho feature.
 * Follows established patterns from useOKRQueries.ts
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/hooks/useTenant'
import { radarKeys } from './radarKeys'
import * as radarAPI from '@/api/radar'
import type {
  RadarResult,
  RadarTreeResult,
  OrgOverviewResult,
  RadarComparisonResult,
  EvaluationMatrixResult,
  EvaluationMatrixDetailedResult,
} from '@/types/radar'

// Cache configuration
const STALE_TIME = 5 * 60 * 1000 // 5 minutes
const GC_TIME = 15 * 60 * 1000 // 15 minutes

// ================================
// RADAR HOOKS
// ================================

/**
 * Use Radar Query
 *
 * Fetches complete radar data for an employee.
 * Cached for 5 minutes.
 *
 * @example
 * ```tsx
 * const { data: radar, isLoading } = useRadar('emp-123')
 * // data = { employee, individual, asLeader, directReports, metadata }
 * ```
 */
export function useRadar(
  employeeId: string | null,
  period?: { startDate?: string; endDate?: string }
) {
  const { tenant } = useTenant()

  return useQuery({
    queryKey: radarKeys.radar(employeeId!, period),
    queryFn: async (): Promise<RadarResult> => {
      if (!tenant?.domain) throw new Error('Tenant domain required')
      if (!employeeId) throw new Error('Employee ID required')
      return await radarAPI.getRadar(tenant.domain, employeeId, period)
    },
    enabled: !!tenant?.domain && !!employeeId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  })
}

// ================================
// ORG OVERVIEW HOOKS
// ================================

/**
 * Use Org Overview Query
 *
 * Fetches organization-wide metrics for the executive landing page.
 * Cached for 5 minutes.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useOrgOverview()
 * // data = { organization, okrs, feedbackHealth, engagement, topPerformers, atRiskAreas }
 * ```
 */
export function useOrgOverview(period?: { startDate?: string; endDate?: string }) {
  const { tenant } = useTenant()

  return useQuery({
    queryKey: radarKeys.orgOverview(period),
    queryFn: async (): Promise<OrgOverviewResult> => {
      if (!tenant?.domain) throw new Error('Tenant domain required')
      return await radarAPI.getOrgOverview(tenant.domain, period)
    },
    enabled: !!tenant?.domain,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  })
}

// ================================
// TREE HOOKS
// ================================

/**
 * Use Radar Tree Query
 *
 * Fetches hierarchical org tree starting from an employee.
 * Cached for 5 minutes.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useRadarTree('emp-123', 3)
 * // data = { root: {...}, totalEmployees: 24, hierarchyDepth: 3 }
 * ```
 */
export function useRadarTree(
  employeeId: string | null,
  depth = 3,
  period?: { startDate?: string; endDate?: string }
) {
  const { tenant } = useTenant()

  return useQuery({
    queryKey: radarKeys.tree(employeeId!, depth, period),
    queryFn: async (): Promise<RadarTreeResult> => {
      if (!tenant?.domain) throw new Error('Tenant domain required')
      if (!employeeId) throw new Error('Employee ID required')
      return await radarAPI.getRadarTree(tenant.domain, employeeId, depth, period)
    },
    enabled: !!tenant?.domain && !!employeeId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  })
}

// ================================
// COMPARISON HOOKS
// ================================

/**
 * Use Radar Comparison Query
 *
 * Fetches side-by-side comparison of two employees.
 * Cached for 5 minutes.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useRadarComparison('emp-123', 'emp-456')
 * // data = { employeeA, employeeB, individual, asLeader, metadata }
 * ```
 */
export function useRadarComparison(
  employeeIdA: string | null,
  employeeIdB: string | null,
  period?: { startDate?: string; endDate?: string }
) {
  const { tenant } = useTenant()

  return useQuery({
    queryKey: radarKeys.comparison(employeeIdA!, employeeIdB!, period),
    queryFn: async (): Promise<RadarComparisonResult> => {
      if (!tenant?.domain) throw new Error('Tenant domain required')
      if (!employeeIdA || !employeeIdB) throw new Error('Both employee IDs required')
      return await radarAPI.compareEmployees(tenant.domain, employeeIdA, employeeIdB, period)
    },
    enabled: !!tenant?.domain && !!employeeIdA && !!employeeIdB,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  })
}

// ================================
// EVALUATION MATRIX HOOKS
// ================================

/**
 * Use Evaluation Matrix Query
 *
 * Fetches evaluation matrix with pillar breakdown and radar chart data.
 * Cached for 5 minutes.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useEvaluationMatrix('emp-123')
 * // data = { pillarSummaries, items, radarChart, evaluatorBreakdown }
 * ```
 */
export function useEvaluationMatrix(
  employeeId: string | null,
  cycleId?: string,
  detailed = false
) {
  const { tenant } = useTenant()

  return useQuery({
    queryKey: radarKeys.matrix(employeeId!, cycleId),
    queryFn: async (): Promise<EvaluationMatrixResult | EvaluationMatrixDetailedResult> => {
      if (!tenant?.domain) throw new Error('Tenant domain required')
      if (!employeeId) throw new Error('Employee ID required')
      return await radarAPI.getEvaluationMatrix(tenant.domain, employeeId, cycleId, detailed)
    },
    enabled: !!tenant?.domain && !!employeeId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  })
}

// ================================
// UTILITY HOOKS
// ================================

/**
 * Invalidate Radar Cache
 *
 * Manually invalidate radar cache to force refetch.
 * Useful for manual refresh buttons.
 *
 * @example
 * ```tsx
 * const invalidateRadar = useInvalidateRadarCache()
 *
 * <Button onClick={() => invalidateRadar()}>
 *   Refresh
 * </Button>
 * ```
 */
export function useInvalidateRadarCache() {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({
      queryKey: radarKeys.all,
    })
  }
}

/**
 * Prefetch Radar
 *
 * Prefetch radar data for an employee (e.g., on hover).
 *
 * @example
 * ```tsx
 * const prefetch = usePrefetchRadar()
 *
 * <Card onMouseEnter={() => prefetch('emp-123')}>
 *   ...
 * </Card>
 * ```
 */
export function usePrefetchRadar() {
  const { tenant } = useTenant()
  const queryClient = useQueryClient()

  return async (employeeId: string, period?: { startDate?: string; endDate?: string }) => {
    if (!tenant?.domain) return

    await queryClient.prefetchQuery({
      queryKey: radarKeys.radar(employeeId, period),
      queryFn: () => radarAPI.getRadar(tenant.domain, employeeId, period),
      staleTime: STALE_TIME,
    })
  }
}
